/**
 * Matriz de anúncios (Fase 2): o que se vende por anúncio. Foto das APIs (ML Matriz/Extrema e Shopee Matriz)
 * + vendas e margem oficial por anúncio (`margens`). Shein pela API de produtos (06/10/2026); TikTok e Amazon pelas vendas.
 * Agrupada pelo SKU do anúncio (kit = SKU próprio): a linha do SKU abre os anúncios.
 */
import { useMemo, useState } from 'react'
import {
  Box, Card, CardContent, Collapse, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import { KpiCard } from '../../components/KpiCard'
import { colunasAnuncio } from './colunas'
import { Seta, TabelaOrdenavel } from './componentes'
import { anunciosFiltrados, anunciosPorSku, brl, type Filtro, nomeLoja, num, pct, resumoAnuncios } from './logic'
import type { NatalData, SkuNatal } from './types'

export function AbaAnuncios({ d, f, ks, onAbrirSku }: { d: NatalData; f: Filtro; ks: SkuNatal[]; onAbrirSku: (sku: string, an?: { loja: string; id: string }) => void }) {
  const [busca, setBusca] = useState('')
  const as = useMemo(() => anunciosFiltrados(d, f, ks, busca).sort((x, y) => y.vendas.fat7 - x.vendas.fat7 || (x.status === 'ativo' ? -1 : 1)), [d, f, ks, busca])
  const r = resumoAnuncios(as)
  const colunas = useMemo(() => colunasAnuncio(d), [d])
  const grupos = useMemo(() => anunciosPorSku(as), [as])
  const [abertos, setAbertos] = useState<Set<string>>(new Set())
  const alternar = (k: string) => setAbertos((a) => { const n = new Set(a); if (n.has(k)) n.delete(k); else n.add(k); return n })
  const semAnuncio = ks.filter((k) => !d.anuncios.some((a) => a.status === 'ativo' && a.comps.some(([s]) => s === k.sku))).length
  return (
    <Stack spacing={2.5}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <KpiCard label="Anúncios ativos" value={num(r.ativos)} hint={`${num(r.pausados)} pausados · ${num(r.soVendas)} vistos só nas vendas`} />
        <KpiCard label="Faturamento 7 dias" value={brl(r.fat7)} hint={`MC oficial ${pct(r.mc7, 1)}`} />
        <KpiCard label="Ativos sem venda em 7 dias" value={num(r.semVenda)} hint="com estoque: revisar preço, foto ou título" />
        <KpiCard label="MC abaixo de 3%" value={num(r.mcBaixa)} hint="no preço atual, antes de ads" />
        <KpiCard label="SKUs sem anúncio ativo" value={num(semAnuncio)} hint="ML, Shopee e Shein pela API; ver Oportunidades" />
      </Stack>
      <Card variant="outlined"><CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap spacing={1}>
          <Box><Typography variant="h6">Matriz de anúncios</Typography>
            <Typography variant="caption" color="text.secondary">{as.length} anúncios em {grupos.length} SKUs de anúncio · ordenados por faturamento 7 dias · clique no SKU para ver os anúncios; no anúncio, para abrir o detalhe</Typography></Box>
          <TextField size="small" placeholder="Buscar título, ID ou SKU" value={busca} onChange={(e) => setBusca(e.target.value)} sx={{ minWidth: 260 }} />
        </Stack>
        {!grupos.length
          ? <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>Nenhum anúncio no filtro.</Typography>
          : (
            <TableContainer sx={{ mt: 1.5, maxHeight: '70vh', border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow>
                  {['SKU do anúncio', 'Anúncios', 'Lojas', 'Peças 7d', 'Fat. 7d'].map((h, i) => (
                    <TableCell key={h} align={i >= 3 ? 'right' : 'left'} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</TableCell>
                  ))}
                </TableRow></TableHead>
                <TableBody>
                  {grupos.map((g) => {
                    const aberto = busca.trim() !== '' || abertos.has(g.chave)
                    const desc = ks.find((k) => k.sku === g.chave)?.descricao ?? d.skus.find((k) => k.sku === g.chave)?.descricao
                    const noAr = g.anuncios.filter((a) => a.status === 'ativo').length
                    return [
                      <TableRow key={g.chave} hover onClick={() => alternar(g.chave)} sx={{ cursor: 'pointer', '& > td': { borderBottom: aberto ? 0 : undefined } }}>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="flex-start"><Seta aberta={aberto} />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={700} color="primary">{g.chave}</Typography>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ maxWidth: 320, whiteSpace: 'normal' }}>
                                {desc ?? `${g.anuncios.some((a) => a.sku_antigo) ? 'SKU antigo de' : 'kit:'} ${[...new Set(g.anuncios.flatMap((a) => a.comps.map((c) => c[0])))].join(' + ')}`}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{num(g.anuncios.length)} · <b>{num(noAr)} no ar</b></TableCell>
                        <TableCell>{[...new Set(g.anuncios.map((a) => nomeLoja(d, a.loja)))].join(', ')}</TableCell>
                        <TableCell align="right">{num(g.anuncios.reduce((x, a) => x + a.vendas.pecas7, 0))}</TableCell>
                        <TableCell align="right">{brl(g.anuncios.reduce((x, a) => x + a.vendas.fat7, 0))}</TableCell>
                      </TableRow>,
                      <TableRow key={`${g.chave}-det`}>
                        <TableCell colSpan={5} sx={{ py: 0, bgcolor: aberto ? 'action.hover' : undefined, borderBottom: aberto ? undefined : 0 }}>
                          <Collapse in={aberto} unmountOnExit>
                            <Box sx={{ my: 1, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                              <TabelaOrdenavel alturaMax={null} vazio="—" linhas={g.anuncios} colunas={colunas} onLinha={(a) => a.comps[0] && onAbrirSku(a.comps[0][0], { loja: a.loja, id: a.id })} />
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>,
                    ]
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Foto dos anúncios: ML {d.meta.fontes.anuncios_ml.lido_em?.slice(0, 16).replace('T', ' ') ?? 'não lida'} · Shopee {d.meta.fontes.anuncios_shopee.lido_em?.slice(0, 16).replace('T', ' ') ?? 'não lida'} · Shein {d.meta.fontes.anuncios_shein?.lido_em?.slice(0, 16).replace('T', ' ') ?? 'pelas vendas'} (UTC).
          Estoque do anúncio fora do Full é o do galpão, compartilhado entre os canais. Peças contam cada componente do kit. MC oficial = margem do sistema nos pedidos dos últimos 7 dias; MC no preço atual = simulação antes de ads (ads entra no DRE abaixo da MC); imposto, e comissão e frete da Shopee, do histórico de 90 dias do anúncio ou SKU só com pelo menos {d.premissas.min_pedidos_historico ?? 10} pedidos, senão da média do marketplace (fonte no título da célula).
        </Typography>
      </CardContent></Card>
    </Stack>
  )
}
